import { defineStore, acceptHMRUpdate } from 'pinia';
import useSelectionStore from '@/stores/selection';
import useUiSubplanningStore from '@/stores/ui/subplanning';
import usePlanningStore from '@/stores/planning/planning';
import usePlanningLanesStore from '@/stores/planning/lanes';
import usePlanningElementsStore from '@/stores/planning/elements';
import usePlanningConfigStore from '@/stores/planning/config';

function getInitialState() {
  return {
    view: null,
    dynamic: null,
    dashboard: null,
    planningsInKanbanMode: new Set(window.safeParseJSON(window.localStorageWrapper.getItem('planningsInKanbanMode'))),
    mode: null, // 'kanban' or null
    projectReportIsOpened: false,
    newElementMenuIsOpened: false,
    newElementMenuPosition: { x: 0, y: 0 },
    newElementMenuLane: null,
    snapshot: false,
    doNotDisplayElementMenu: null,
    doNotUpdateSelection: null,
    doNotResetSelection: null,
    displayDependenciesErrors: false,
    errors: null,
    exportImportOpeningTab: '',
    planningDrawerTabIndex: '',
    openedElement: null,
    openedColorPickerElement: null,
    openedDependenciesElement: null,
    openedMeetingElement: null,
    wrapperData: {},
    clickOnPlanning: 0, // last timestamp of click on planning, for click detection (closing drawer)
  };
}

const store = defineStore('ui-planning', {
  state: () => getInitialState(),

  actions: {
    reset(values) {
      _.extend(this, getInitialState(), values);
    },
    loadPlanningMode({ planningId }) {
      this.mode = this.planningsInKanbanMode.has(planningId) ? 'kanban' : null;
    },
    savePlanningMode({ mode, planningId }) {
      if (mode == 'kanban') {
        this.planningsInKanbanMode.add(planningId);
      } else {
        this.planningsInKanbanMode.delete(planningId);
      }
      window.localStorageWrapper.setItem('planningsInKanbanMode', JSON.stringify([...this.planningsInKanbanMode]));
    },
    openExportImport(val = '') {
      this.exportImportOpeningTab = val;
    },
    openElement(el = null) {
      this.openedElement = el;
      this.planningDrawerTabIndex = el ? 'element' : '';
    },
    openColorPickerElement(el = null) {
      this.openedColorPickerElement = el;
      this.planningDrawerTabIndex = el ? 'color-picker' : '';
    },
    openDependenciesElement(el = null) {
      this.openedDependenciesElement = el;
      this.planningDrawerTabIndex = el ? 'dependencies' : '';
    },
    openMeetingElement(el = null) {
      this.openedMeetingElement = el;
      this.planningDrawerTabIndex = el ? 'meeting' : '';
    },
    openConfigPanel(val = false) {
      this.planningDrawerTabIndex = val ? 'config' : '';
    },
    updateWrapperData($wrapper) {
      this.wrapperData = { width: $wrapper.width(), position: $wrapper.position(), scrollLeft: $wrapper.scrollLeft() };
    },
    setDisplayDependenciesErrors(newVal) {
      this.displayDependenciesErrors = newVal;
    },
    addElement({ type, el, x, y, laneId, silent = false }) {
      const { planning } = usePlanningStore();
      let positionLeft;
      const helperwidth = type == 'milestone' ? 70 : 130;
      if (! planning.lanes.length) return Promise.resolve(null);
      // get lane
      let lane;
      if (laneId) {
        lane = planning.lanes.find(item => item.id == laneId);
      } else if (el) {
        lane = planning.lanes.find(item => item.id == el.getLaneId());
      } else if (! y) {
        [lane] = planning.lanes;
      } else { // draggableButton
        let i = 0;
        const lanes = usePlanningLanesStore().getVisibleLanes({ planning });
        while (lanes[i] && $(`#lane${lanes[i].id}`).offset().top < y) i += 1;
        i -= 1;
        if (i < 0) return Promise.resolve(null);
        lane = lanes[i];
      }
      if (this.view && lane.access_right != 'modify') return Promise.resolve(null);
      if (x) {
        const $col = $(`#lane${lane.id}`);
        positionLeft = x - helperwidth / 2 - $col.offset().left;
        if (positionLeft < -helperwidth || positionLeft > $col.width()) return Promise.resolve(null);
      }
      const extraProps = this.view ? { access_right: 'modify' } : null;
      const newels = usePlanningElementsStore().addElements({ argstab: [{ type, lane, el, positionLeft, y }], silent, extraProps });
      if (! silent) useSelectionStore().reset(newels.map(item => item.id));
      return newels;
    },
    deleteElement(el) {
      if (this.openedElement && this.openedElement.id == el.id) this.openElement(null);
      usePlanningElementsStore().deleteElements({ els: [el] });
    },
    scrollToEl({ el, open = false, scrollX = true, scrollY = true }) {
      if (! el) return;
      const $el = document.getElementById(`el${el.id}`);
      if ($el) {
        const { top: elTop, width: elWidth } = $el.getBoundingClientRect();
        if (scrollY) {
          const top = elTop + (window.pageYOffset || document.documentElement.scrollTop);
          setTimeout(() => { window.scrollTo({ top: top - 220, behavior: 'smooth' }); });
        }
        if (scrollX) {
          const $tableWrapper = document.getElementById('table-wrapper');
          if ($tableWrapper) {
            const contentAlignmentMargin = $tableWrapper.getBoundingClientRect().width - 462 - 100 - elWidth; // 462 = width of planning drawer, 100 = margin with planning drawer
            $tableWrapper.scrollTo({ left: $el.offsetLeft - Math.max(0, contentAlignmentMargin), behavior: 'smooth' });
          }
        }
      }
      if (open) {
        useSelectionStore().reset([el.id]);
        this.openElementDetails(el);
      }
    },
    openElementDetails(el) {
      const elNode = document.getElementById(`el${el.id}`);
      if (elNode && (elNode.classList.contains('is-dragging') || elNode.classList.contains('is-resizing'))) return; // do not open details after dragging / resize
      usePlanningElementsStore().startChangingElement();
      usePlanningConfigStore().startChangingConfig();
      this.openElement(el);
    },
    closeElementDetails(reason) {
      if (! this.openedElement) return;
      if (this.openedElement.dashboard_el) { // subproject
        useUiSubplanningStore().closeElementDetails(reason);
        return;
      }
      if (reason == 'delete') {
        this.deleteElement(this.openedElement);
      } else {
        usePlanningConfigStore().changingConfig();
        usePlanningElementsStore().changingElement(this.openedElement);
      }
      this.openElement(null);
    },
    openColorPicker(el) {
      usePlanningElementsStore().startChangingElement();
      usePlanningConfigStore().startChangingConfig();
      this.openColorPickerElement(el);
    },
    closeColorPicker() {
      if (! this.openedColorPickerElement) return;
      if (this.openedColorPickerElement.dashboard_el) { // subproject
        useUiSubplanningStore().closeColorPicker();
        return;
      }
      usePlanningConfigStore().changingConfig();
      usePlanningElementsStore().changingElement(this.openedColorPickerElement);
      this.openColorPickerElement(null);
    },
    openDependencies(el) {
      usePlanningElementsStore().startChangingElement();
      usePlanningConfigStore().startChangingConfig();
      this.openDependenciesElement(el);
    },
    closeDependencies() {
      if (! this.openedDependenciesElement) return;
      usePlanningConfigStore().changingConfig();
      usePlanningElementsStore().changingElement();
      this.openedDependenciesElement = null;
    },
    openConfig() {
      this.openConfigPanel(true);
    },
    closeConfig() {
      this.openConfigPanel(false);
    },
    openMeeting(el) {
      this.openMeetingElement(el);
    },
    closeMeeting() {
      this.openMeetingElement(null);
    },
    openProjectReport() {
      this.projectReportIsOpened = true;
    },
    closeProjectReport() {
      this.projectReportIsOpened = false;
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));

export default store;
